import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, CampoLongo, Cartao } from "../ui";
import { SugestoesDeArtigos } from "../conhecimento";
import { SeletorDeServico } from "../operacao/SeletorDeServico";
import { CamposNaAbertura } from "./CamposNaAbertura";
import { useAjudaAntesDeAbrir } from "../../nucleo/ajudaAntesDeAbrir";
import { mensagemDoConsole } from "../../nucleo/console";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";
import { MIME_DE_IMAGEM, MIME_DE_VIDEO, megabytes, motivoDeRecusaDoAnexo } from "../../nucleo/anexosDoChamado";
import {
  useAbrirChamadoPeloFormulario,
  useOpcoesDoFormulario,
  type OpcoesDoFormulario,
} from "../../nucleo/tickets";
import {
  pendentesDaAbertura,
  useDefinicoesDeCampo,
  valoresParaEnviar,
} from "../../nucleo/tickets-avancado";

function tamanhoLegivel(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function Opcoes({
  legenda,
  nome,
  opcoes,
  valor,
  aoMudar,
  ajuda,
}: {
  legenda: string;
  nome: string;
  opcoes: readonly { chave: string; rotulo: string }[];
  valor: string;
  aoMudar: (chave: string) => void;
  ajuda?: string;
}) {
  return (
    <fieldset className="campo" data-testid={`opcoes-${nome}`}>
      <legend className="campo__rotulo">{legenda}</legend>
      {ajuda ? <p className="nota-rodape mb-1">{ajuda}</p> : null}
      <div className="grid gap-1">
        {opcoes.map((opcao) => (
          <label key={opcao.chave} className="flex items-center gap-2">
            <input
              type="radio"
              name={nome}
              value={opcao.chave}
              checked={valor === opcao.chave}
              onChange={() => aoMudar(opcao.chave)}
            />
            <span>{opcao.rotulo}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Linha do painel da direita: "o que já sabemos" ou "como o atendimento vai receber". */
function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <dt className="nota-rodape">{rotulo}</dt>
      <dd className="text-right font-medium">{valor}</dd>
    </div>
  );
}

function PainelDaDireita({
  opcoes,
  modulo,
  tipo,
  impacto,
}: {
  opcoes: OpcoesDoFormulario;
  modulo: string;
  tipo: string;
  impacto: string;
}) {
  const auto = opcoes.automatico;
  const moduloEscolhido = opcoes.modulos.find((item) => item.chave === modulo);
  const tipoEscolhido = opcoes.tipos.find((item) => item.chave === tipo);
  const impactoEscolhido = opcoes.impactos.find((item) => item.chave === impacto);
  return (
    <aside className="grid gap-4" aria-label="Resumo do chamado">
      <Cartao titulo="O que vai junto automaticamente">
        <p className="nota-rodape mb-2">O portal já sabe quem você é — nada disso é perguntado.</p>
        <dl data-testid="vai-junto">
          <Linha rotulo="Empresa" valor={auto.empresa} />
          <Linha rotulo="Quem abriu" valor={`${auto.quem_nome} · ${auto.quem_email}`} />
          <Linha
            rotulo="Ambiente e versão"
            valor={
              auto.ambiente || auto.versao
                ? [auto.ambiente, auto.versao].filter(Boolean).join(" · ")
                : "Não informado"
            }
          />
          <Linha rotulo="Responsável de TI" valor={auto.responsavel_ti ?? "Não cadastrado"} />
        </dl>
      </Cartao>
      <Cartao titulo="Como o atendimento vai receber">
        <dl data-testid="como-nasce">
          <Linha rotulo="Módulo" valor={moduloEscolhido?.rotulo ?? "—"} />
          <Linha rotulo="Tipo do chamado" valor={tipoEscolhido?.no_jira ?? "—"} />
          <Linha rotulo="Prioridade" valor={impactoEscolhido?.no_jira ?? "—"} />
          <Linha rotulo="Origem" valor={opcoes.origem} />
          <Linha rotulo="Canal de entrada" valor={opcoes.canal_de_entrada} />
          <Linha rotulo="Responsável" valor="Sem responsável" />
        </dl>
        <p className="nota-rodape mt-2">
          A prioridade que você informa é uma declaração de impacto: a equipe pode reclassificar.
        </p>
      </Cartao>
    </aside>
  );
}

/** Formulário de abertura do chamado do cliente (item 11 do DOCX da Amanda, tela 12). */
export function FormularioDeChamado() {
  const opcoes = useOpcoesDoFormulario();
  const abrir = useAbrirChamadoPeloFormulario();
  const voltarParaHome = useVoltarParaHome();
  const idDosAnexos = useId();
  const campoDeArquivo = useRef<HTMLInputElement>(null);
  const [modulo, setModulo] = useState("");
  const [tipo, setTipo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [impacto, setImpacto] = useState("");
  const [anexos, setAnexos] = useState<File[]>([]);
  const [recusaDeAnexo, setRecusaDeAnexo] = useState("");
  const ajuda = useAjudaAntesDeAbrir(descricao);
  const definicoes = useDefinicoesDeCampo();
  const [valoresDosCampos, setValoresDosCampos] = useState<Record<string, unknown>>({});
  const [servicoEscolhido, setServicoEscolhido] = useState<{ id: string; categoria: string; servico: string } | null>(null);
  const pendentes = pendentesDaAbertura(definicoes.data ?? [], valoresDosCampos);

  if (opcoes.isPending) return <p role="status">Carregando o formulário…</p>;
  if (opcoes.isError) return <Aviso tom="erro">{mensagemDoConsole(opcoes.error)}</Aviso>;
  const dados = opcoes.data;
  const exigeModulo = dados.modulos.length > 0;
  const completo = Boolean(
    (!exigeModulo || modulo) && tipo && impacto && descricao.trim() && pendentes.length === 0,
  );

  function escolherArquivos(lista: FileList | null) {
    const recusas: string[] = [];
    const aceitos = [...anexos];
    for (const arquivo of Array.from(lista ?? [])) {
      const motivo = motivoDeRecusaDoAnexo(arquivo, aceitos, dados.anexos);
      if (motivo) recusas.push(motivo);
      else aceitos.push(arquivo);
    }
    setAnexos(aceitos);
    setRecusaDeAnexo(recusas.join(" "));
    // Zera o campo: escolher o mesmo arquivo de novo (depois de tirá-lo) precisa disparar `change`.
    if (campoDeArquivo.current) campoDeArquivo.current.value = "";
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!completo) return;
    const campos = valoresParaEnviar(definicoes.data ?? [], valoresDosCampos);
    abrir.mutate(
      {
        tipo,
        impacto,
        modulo,
        descricao: descricao.trim(),
        anexos,
        ...(servicoEscolhido ? { categoria: servicoEscolhido.categoria, servico: servicoEscolhido.servico } : {}),
        ...(Object.keys(campos).length > 0 ? { campos } : {}),
      },
      {
        onSuccess: (ticket) => {
          ajuda.registrarAbertura(descricao);
          voltarParaHome(`Chamado #${ticket.numero} aberto.`);
        },
      },
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem] mb-6">
      <Cartao titulo="Abrir um chamado">
        <form onSubmit={enviar} data-testid="novo-chamado">
          {exigeModulo ? (
            <label className="campo" htmlFor="modulo-do-chamado">
              <span className="campo__rotulo">
                Módulo <span aria-hidden="true">*</span>
              </span>
              <select
                id="modulo-do-chamado"
                className="campo__controle"
                value={modulo}
                required
                onChange={(evento) => setModulo(evento.target.value)}
              >
                <option value="">Escolha o módulo</option>
                {dados.modulos.map((item) => (
                  <option value={item.chave} key={item.chave}>{item.rotulo}</option>
                ))}
              </select>
            </label>
          ) : null}
          <Opcoes
            legenda="Tipo *"
            nome="tipo"
            opcoes={dados.tipos}
            valor={tipo}
            aoMudar={setTipo}
          />
          <CampoLongo
            rotulo="O que está acontecendo?"
            obrigatorio
            value={descricao}
            onChange={(evento) => {
              setDescricao(evento.target.value);
              ajuda.recomecar();
            }}
            placeholder="Conte o que você fez, o que esperava e o que aconteceu. Se der, informe o número do pedido ou o representante envolvido."
            required
          />
          <SugestoesDeArtigos
            ajuda={ajuda}
            aoDesistir={() => {
              setDescricao("");
            }}
          />
          <Opcoes
            legenda="Está impedindo o trabalho agora? *"
            nome="impacto"
            opcoes={dados.impactos}
            valor={impacto}
            aoMudar={setImpacto}
            ajuda={'Use "parou tudo" só quando ninguém consegue trabalhar — é o que mantém essa marcação significando alguma coisa.'}
          />
          <SeletorDeServico valor={servicoEscolhido?.id ?? ""} aoMudar={setServicoEscolhido} />
          <CamposNaAbertura definicoes={definicoes.data ?? []} valores={valoresDosCampos} aoMudar={setValoresDosCampos} />
          <div className="campo">
            <label className="campo__rotulo" htmlFor={idDosAnexos}>Anexar print ou vídeo</label>
            <p className="nota-rodape mb-1">
              {dados.anexos.formatos.join(", ")} · até {dados.anexos.maximo} arquivos · print até{" "}
              {megabytes(dados.anexos.imagem_bytes)}, vídeo até {megabytes(dados.anexos.video_bytes)}.
            </p>
            <input
              id={idDosAnexos}
              ref={campoDeArquivo}
              type="file"
              multiple
              accept={[...MIME_DE_IMAGEM, ...MIME_DE_VIDEO].join(",")}
              onChange={(evento) => escolherArquivos(evento.target.files)}
            />
            {recusaDeAnexo ? <Aviso tom="atencao" className="mt-2" data-testid="recusa-de-anexo">{recusaDeAnexo}</Aviso> : null}
            {anexos.length > 0 ? (
              <ul className="mt-2" data-testid="anexos-escolhidos">
                {anexos.map((arquivo, indice) => (
                  <li key={`${arquivo.name}-${indice}`} className="flex items-center gap-2">
                    <span>{arquivo.name} · {tamanhoLegivel(arquivo.size)}</span>
                    <Botao
                      type="button"
                      tamanho="pequeno"
                      variante="fantasma"
                      aria-label={`Remover ${arquivo.name}`}
                      onClick={() => {
                        setAnexos(anexos.filter((_, posicao) => posicao !== indice));
                        setRecusaDeAnexo("");
                      }}
                    >
                      Remover
                    </Botao>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {abrir.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(abrir.error)}</Aviso> : null}
          <Botao type="submit" disabled={!completo || abrir.isPending}>
            {abrir.isPending ? "Abrindo…" : "Abrir chamado"}
          </Botao>
        </form>
      </Cartao>
      <PainelDaDireita opcoes={dados} modulo={modulo} tipo={tipo} impacto={impacto} />
    </div>
  );
}
